import {Routes} from '@/config/routes';
import {ProductQueryParams} from '@/lib/constants';
import {isSearchLoadingAtom, searchQueryAtom} from '@/store/search-atom';
import {useAtom} from 'jotai';
import {debounce, isArray} from 'lodash';
import {usePathname, useSearchParams, useRouter} from 'next/navigation';
import {ChangeEvent, KeyboardEventHandler, useCallback, useEffect, useState} from 'react';

function useProductSearch() {
    const router = useRouter();
    const query = useSearchParams();
    const pathName = usePathname();
    const isSearchPage = pathName === Routes.search;
    const searchValue = query.get(ProductQueryParams.SEARCH);
    const searchString = isArray(searchValue) ? searchValue.join(' ') : (searchValue ?? '');

    const [searchQuery, setSearchQuery] = useState(searchString);
    const [, setQueryAtom] = useAtom(searchQueryAtom);
    const [isChangingRoute, setIsChangingRoute] = useState(false);
    const [isSearchLoading, setIsSearchLoading] = useAtom(isSearchLoadingAtom);

    useEffect(() => {
        setIsSearchLoading(false);
    }, [pathName, query]);

    useEffect(() => {
        if (isChangingRoute && !isSearchLoading) {
            setIsSearchLoading(true);
        }
    }, [isSearchLoading, isChangingRoute]);

    const handleSearch = (searchString: string) => {
        if (!searchString) return;

        setIsChangingRoute(true);

        const params = handleUpdateSearchParams(searchString);

        if (isSearchPage) {
            router.replace(`${pathName}?${params}`);
        } else {
            router.push(`${Routes.search}?${params}`);
        }
    };

    const handleUpdateSearchParams = useCallback(
        (newQuery: string) => {
            const params = new URLSearchParams();
            params.set(ProductQueryParams.SEARCH, newQuery);
            return params.toString();
        },
        [router, query],
    );

    const debounceSearch = useCallback(
        debounce((value: string) => {
            if (isSearchPage) {
                //@: Update query params by newest input
                const newParams = handleUpdateSearchParams(value);
                router.replace(`${pathName}?${newParams}`);

                //@: Update query at client side
                setQueryAtom(value);
            } else {
                setQueryAtom('');
            }
            setSearchQuery(value);
        }, 500),
        [handleUpdateSearchParams],
    );

    const handleOnChange = (e: ChangeEvent<HTMLInputElement>) => {
        const {value} = e.target;
        debounceSearch(value);
    };

    const handleEnterPress: KeyboardEventHandler<HTMLInputElement> = e => {
        if (e.key === 'Enter') {
            const value = (e.target as HTMLInputElement)?.value;
            if (value) {
                handleSearch(value);
                setSearchQuery(value);
            }
        }
    };

    const onSelect = () => {
        return;
    };

    return {
        isSearchPage,
        searchQuery,
        handleSearch,
        handleOnChange,
        handleEnterPress,
        onSelect,
    };
}

export default useProductSearch;
